import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from '../hooks/useInView';
import { projects } from '../data/portfolio';

const FILTERS = ['All', 'AI / ML', 'C++', 'Node.js', 'Computer Vision', 'Healthcare', 'Enterprise', 'Data Science', 'E-Commerce'];

function ProjectCard({ project, index }) {
  const [hovered, setHovered] = useState(false);
  const [ref, inView] = useInView(0.08);

  return (
    <motion.div
      ref={ref} 
      initial={{ opacity: 0, y: 60, scale: 0.95 }}
      animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: 0.7, delay: (index % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        background: hovered ? 'rgba(0,245,255,0.06)' : 'rgba(0,245,255,0.03)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: `1px solid ${hovered ? project.color + '50' : 'rgba(0,245,255,0.1)'}`,
        borderRadius: '18px',
        padding: 'clamp(1.25rem, 3vw, 1.75rem)',
        cursor: 'default',
        transition: 'all 0.35s ease',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        boxShadow: hovered ? `0 20px 60px ${project.color}20, 0 0 40px ${project.color}10` : '0 4px 20px rgba(0,0,0,0.3)',
        display: 'flex', flexDirection: 'column', gap: '1rem',
        overflow: 'hidden',
      }}
    >
      {/* Top glow line */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: hovered ? 2 : 1,
        background: `linear-gradient(to right, transparent, ${project.color}, transparent)`,
        boxShadow: hovered ? `0 0 20px ${project.color}` : 'none',
        transition: 'all 0.35s',
      }} />

      {/* Background orb */}
      <div style={{
        position: 'absolute', top: -40, right: -40,
        width: 150, height: 150, borderRadius: '50%',
        background: project.color,
        opacity: hovered ? 0.06 : 0.02,
        filter: 'blur(40px)',
        transition: 'opacity 0.35s',
        pointerEvents: 'none',
      }} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{
            display: 'inline-block',
            fontFamily: 'var(--font-display)',
            fontSize: '0.55rem',
            letterSpacing: '0.12em',
            padding: '0.2rem 0.65rem',
            background: project.color + '18',
            border: `1px solid ${project.color}35`,
            borderRadius: '4px',
            color: project.color,
            textTransform: 'uppercase',
            marginBottom: '0.6rem',
            boxShadow: hovered ? `0 0 12px ${project.color}30` : 'none',
            transition: 'box-shadow 0.3s',
          }}>
            {project.tag}
          </span>
          <h3 style={{
            fontFamily: 'var(--font-heading)',
            fontWeight: 700,
            fontSize: 'clamp(1rem, 2vw, 1.15rem)',
            color: 'var(--white)',
            lineHeight: 1.2,
            marginBottom: '0.2rem',
          }}>
            {project.title}
          </h3>
          <p style={{ fontFamily: 'var(--font-display)', fontSize: '0.6rem', color: 'var(--grey)', letterSpacing: '0.05em' }}>
            {project.subtitle}
          </p>
        </div>
        <div style={{
          fontFamily: 'var(--font-display)',
          fontSize: '0.55rem',
          color: 'var(--grey2)',
          letterSpacing: '0.05em',
          whiteSpace: 'nowrap',
          marginLeft: '0.75rem',
          marginTop: '0.2rem',
        }}>
          {project.period}
        </div>
      </div>

      <p style={{ fontSize: '0.84rem', color: 'var(--grey)', lineHeight: 1.7, position: 'relative' }}>
        {project.description}
      </p>

      {/* Highlights */}
      <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.4rem', position: 'relative' }}>
        {project.highlights.map((h, i) => (
          <li key={i} style={{ display: 'flex', gap: '0.6rem', fontSize: '0.78rem', color: 'var(--grey)', lineHeight: 1.5, alignItems: 'flex-start' }}>
            <span style={{ color: project.color, flexShrink: 0, marginTop: '0.25rem', fontSize: '0.5rem', textShadow: `0 0 6px ${project.color}` }}>◆</span>
            {h}
          </li>
        ))}
      </ul>

      {/* Stack tags */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: 'auto', position: 'relative' }}>
        {project.stack.map(tech => (
          <span key={tech} style={{
            fontFamily: 'var(--font-display)',
            fontSize: '0.55rem',
            letterSpacing: '0.06em',
            padding: '0.2rem 0.55rem',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(0,245,255,0.1)',
            borderRadius: '4px',
            color: 'var(--grey)',
          }}>
            {tech}
          </span>
        ))}
      </div>

      {/* Footer */}
<div
  style={{
    display: 'flex',
    gap: '0.75rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid rgba(0,245,255,0.07)',
    position: 'relative',
  }}
>
  {/* GitHub */}
  <a
    href={project.github}
    target="_blank"
    rel="noopener noreferrer"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.4rem',
      fontFamily: 'var(--font-display)',
      fontSize: '0.6rem',
      letterSpacing: '0.08em',
      color: 'var(--grey)',
      padding: '0.4rem 0.9rem',
      border: '1px solid rgba(0,245,255,0.1)',
      borderRadius: '6px',
      background: 'rgba(0,245,255,0.02)',
      transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
      transform: 'translateY(0)',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.color = 'var(--cyan)';
      e.currentTarget.style.borderColor = 'rgba(0,245,255,0.35)';
      e.currentTarget.style.background = 'rgba(0,245,255,0.06)';
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,245,255,0.12)';
      e.currentTarget.querySelector('svg').style.transform = 'translateX(-2px)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.color = 'var(--grey)';
      e.currentTarget.style.borderColor = 'rgba(0,245,255,0.1)';
      e.currentTarget.style.background = 'rgba(0,245,255,0.02)';
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
      e.currentTarget.querySelector('svg').style.transform = 'translateX(0)';
    }}
  >
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="currentColor"
      style={{ transition: 'transform 0.25s ease' }}
    >
      <path d="M12 0c-6.626 0-12 5.373-12 12..." />
    </svg>
    GitHub
  </a>

  {/* Live Demo */}
  {project.live ? (
    <a
      href={project.live}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.4rem',
        fontFamily: 'var(--font-display)',
        fontSize: '0.6rem',
        letterSpacing: '0.08em',
        color: project.color,
        padding: '0.4rem 0.9rem',
        border: `1px solid ${project.color}35`,
        borderRadius: '6px',
        background: project.color + '10',
        transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        transform: 'translateY(0)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = project.color + '20';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 8px 22px ${project.color}25`;
        e.currentTarget.querySelector('svg').style.transform = 'translateX(2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = project.color + '10';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.querySelector('svg').style.transform = 'translateX(0)';
      }}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        style={{ transition: 'transform 0.25s ease' }}
      >
        <path d="M18 13v6a2 2 0 0 1-2 2H5..." />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
      </svg>
      Live Demo
    </a>
  ) : (
    <span
      style={{
        fontFamily: 'var(--font-display)',
        fontSize: '0.58rem',
        color: 'var(--grey2)',
        padding: '0.4rem 0.75rem',
        alignSelf: 'center',
        letterSpacing: '0.06em',
        transition: 'opacity 0.2s',
      }}
    >
      Private / Internal
    </span>
  )}
</div>
    </motion.div>
  );
}

export default function Projects() {
  const [ref, inView] = useInView(0.1);
  const [filter, setFilter] = useState('All');
  const filtered = filter === 'All' ? projects : projects.filter(p => p.tag === filter);

  return (
    <section id="projects" className="section" style={{ background: 'linear-gradient(to bottom, var(--bg), var(--bg2), var(--bg))' }}>
      <div style={{ position: 'absolute', top: 0, left: '5%', right: '5%', height: 1, background: 'linear-gradient(to right, transparent, rgba(0,245,255,0.08), transparent)' }} />
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '3rem' }}
        >
          <div className="sec-tag">02 · Projects</div>
          <h2 className="sec-title" style={{ marginBottom: '1rem' }}>
            Things I've{' '}
            <span style={{
              background: 'linear-gradient(90deg, var(--cyan), var(--teal))',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>
              built & shipped
            </span>
          </h2>
          <p style={{ color: 'var(--grey)', maxWidth: 480, fontSize: '0.92rem', lineHeight: 1.75, marginBottom: '2rem' }}>
            Real-world systems with measurable outcomes — from AI pipelines to enterprise backends.
          </p>

          {/* Filters */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {FILTERS.map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '0.58rem',
                  letterSpacing: '0.08em',
                  padding: '0.38rem 0.9rem',
                  borderRadius: '6px',
                  border: `1px solid ${filter === f ? 'rgba(0,245,255,0.4)' : 'rgba(0,245,255,0.1)'}`,
                  background: filter === f ? 'rgba(0,245,255,0.1)' : 'rgba(0,245,255,0.02)',
                  color: filter === f ? 'var(--cyan)' : 'var(--grey)',
                  textTransform: 'uppercase',
                  transition: 'all 0.2s',
                  backdropFilter: 'blur(10px)',
                  boxShadow: filter === f ? '0 0 15px rgba(0,245,255,0.15)' : 'none',
                }}
              >
                {f}
              </button>
            ))}
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={filter}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 350px), 1fr))',
              gap: '1.25rem',
            }}
          >
            {filtered.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
